import React, { useState } from 'react';
import {
  Shield,
  Plus,
  Trash2,
  Edit2,
  Check,
  X,
  RefreshCw,
  AlertTriangle,
  Info,
  Loader2,
  Monitor,
} from 'lucide-react';
import { useWidgetTheme, mainAppTheme } from '../widget/useWidgetTheme';

interface IpEntry {
  id: number;
  ip_cidr: string;
  label: string | null;
  created_at: string;
}

interface IPRestrictionsCoreProps {
  enabled: boolean;
  entries: IpEntry[];
  yourIp: string;
  loading: boolean;
  error: string | null;
  refreshing?: boolean;
  mode: 'modal' | 'widget';
  theme?: any;
  onSetEnabled: (enabled: boolean) => Promise<void>;
  onAddEntry: (ipCidr: string, label: string | null) => Promise<void>;
  onUpdateEntry: (entryId: number, label: string | null) => Promise<void>;
  onDeleteEntry: (entryId: number) => Promise<void>;
  onRefresh: () => Promise<void>;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

const IPRestrictionsCore: React.FC<IPRestrictionsCoreProps> = ({
  enabled,
  entries,
  yourIp,
  loading,
  error,
  refreshing = false,
  mode,
  theme: themeOverrides,
  onSetEnabled,
  onAddEntry,
  onUpdateEntry,
  onDeleteEntry,
  onRefresh,
  onSuccess,
  onError,
}) => {
  const widgetTheme = useWidgetTheme();
  const baseTheme = mode === 'modal' ? mainAppTheme : widgetTheme;
  const theme = themeOverrides ? { ...baseTheme, ...themeOverrides } : baseTheme;

  // Form state
  const [newIp, setNewIp] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [adding, setAdding] = useState(false);
  const [toggling, setToggling] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [saving, setSaving] = useState(false);

  // Delete modal state
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; entry: IpEntry | null }>({
    open: false,
    entry: null,
  });
  const [deleting, setDeleting] = useState(false);

  // Hover states
  const [addBtnHover, setAddBtnHover] = useState(false);
  const [refreshBtnHover, setRefreshBtnHover] = useState(false);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);

  const handleToggleEnabled = async () => {
    setToggling(true);
    try {
      await onSetEnabled(!enabled);
      onSuccess?.(enabled ? 'IP restrictions disabled' : 'IP restrictions enabled');
    } catch (err: any) {
      onError?.(err.message || 'Failed to update settings');
    } finally {
      setToggling(false);
    }
  };

  const handleAddEntry = async () => {
    if (!newIp.trim()) return;
    setAdding(true);
    try {
await onAddEntry(newIp.trim(), newLabel.trim() || null);
      setNewIp('');
      setNewLabel('');
      onSuccess?.(enabled ? 'IP address added' : 'IP added — click Enable Restrictions to activate');
    } catch (err: any) {
      onError?.(err.message || 'Failed to add IP address');
    } finally {
      setAdding(false);
    }
  };

  const handleAddCurrentIp = async () => {
    if (!yourIp) return;
    setAdding(true);
    try {
await onAddEntry(yourIp, 'My current IP');
      onSuccess?.(enabled ? 'Your current IP added' : 'IP added — click Enable Restrictions to activate');
    } catch (err: any) {
      onError?.(err.message || 'Failed to add IP address');
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (entry: IpEntry) => {
    setEditingId(entry.id);
    setEditLabel(entry.label || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditLabel('');
  };

  const handleSaveEdit = async (entryId: number) => {
    setSaving(true);
    try {
      await onUpdateEntry(entryId, editLabel.trim() || null);
      setEditingId(null);
      setEditLabel('');
      onSuccess?.('Label updated');
    } catch (err: any) {
      onError?.(err.message || 'Failed to update label');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteModal = (entry: IpEntry) => {
    setDeleteModal({ open: true, entry });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ open: false, entry: null });
    setDeleting(false);
  };

  const handleDeleteEntry = async () => {
    if (!deleteModal.entry) return;
    setDeleting(true);
    try {
      await onDeleteEntry(deleteModal.entry.id);
      onSuccess?.('IP address removed');
      closeDeleteModal();
    } catch (err: any) {
      onError?.(err.message || 'Failed to remove IP address');
      setDeleting(false);
    }
  };

  const handleRefresh = async () => {
    try {
      await onRefresh();
      onSuccess?.('Refreshed');
    } catch (err: any) {
      onError?.(err.message || 'Failed to refresh');
    }
  };

  const isCurrentIpInList = entries.some(e => e.ip_cidr === yourIp);

  // Error state
  if (error && !loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          backgroundColor: theme.colors.errorBg,
          border: `1px solid ${theme.colors.error}`,
          borderRadius: theme.radius.md,
          padding: '16px',
        }}
      >
        <AlertTriangle size={20} strokeWidth={1.5} color={theme.colors.error} style={{ flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 500, color: theme.colors.error }}>{error}</p>
        </div>
        <button
          onClick={onRefresh}
          style={{
            padding: '6px 12px',
            backgroundColor: theme.colors.error,
            color: '#ffffff',
            border: 'none',
            borderRadius: theme.radius.sm,
            fontSize: '0.8125rem',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: theme.fontSans, color: theme.colors.text }}>
      {/* Header */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: theme.radius.lg,
              background: `linear-gradient(135deg, ${theme.colors.primary} 0%, #818cf8 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 6px -1px rgba(79, 70, 229, 0.3)',
            }}
          >
            <Shield size={24} strokeWidth={1.5} color="#ffffff" />
          </div>
          <div>
            <h1
              style={{
                margin: 0,
                fontSize: '1.5rem',
                fontWeight: 700,
                letterSpacing: '-0.02em',
                color: theme.colors.text,
              }}
            >
              IP Restrictions
            </h1>
            <p style={{ margin: 0, fontSize: '0.875rem', color: theme.colors.textTertiary }}>
              Control access by IP address
            </p>
          </div>
        </div>

        <button
          onClick={handleRefresh}
          disabled={refreshing}
          onMouseEnter={() => setRefreshBtnHover(true)}
          onMouseLeave={() => setRefreshBtnHover(false)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 14px',
            backgroundColor: refreshing ? theme.colors.bgAlt : refreshBtnHover ? theme.colors.bgAlt : theme.colors.bg,
            color: theme.colors.textMuted,
            border: `1px solid ${theme.colors.border}`,
            borderRadius: theme.radius.md,
            fontSize: '0.875rem',
            fontWeight: 500,
            cursor: refreshing ? 'not-allowed' : 'pointer',
            transition: 'all 0.15s ease',
          }}
        >
          {refreshing ? (
            <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
          ) : (
            <RefreshCw size={16} strokeWidth={1.5} />
          )}
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

{/* Enable/Disable Toggle Card */}
      <div
        style={{
          backgroundColor: theme.colors.bg,
          borderRadius: theme.radius.lg,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: `2px solid ${enabled ? theme.colors.success : theme.colors.border}`,
          padding: '24px',
          marginBottom: '24px',
        }}
      >
        {/* Status Banner */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 16px',
            marginBottom: '20px',
            borderRadius: theme.radius.md,
            backgroundColor: enabled ? theme.colors.successBg : theme.colors.bgAlt,
          }}
        >
          <div
            style={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              backgroundColor: enabled ? theme.colors.success : theme.colors.textMuted,
              boxShadow: enabled ? `0 0 8px ${theme.colors.success}` : 'none',
            }}
          />
          <span
            style={{
              fontSize: '0.9375rem',
              fontWeight: 600,
              color: enabled ? theme.colors.success : theme.colors.textMuted,
            }}
          >
            {enabled ? 'IP RESTRICTIONS ACTIVE' : 'IP RESTRICTIONS DISABLED'}
          </span>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: theme.colors.text }}>
              {enabled ? 'Access Restricted' : 'Open Access'}
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: '0.875rem', color: theme.colors.textMuted }}>
              {enabled
                ? `Only ${entries.length} allowed IP address${entries.length !== 1 ? 'es' : ''} can access your workspace`
                : 'Anyone can access your workspace (no IP filtering)'}
            </p>
          </div>
          <button
            onClick={handleToggleEnabled}
            disabled={toggling || loading || (!enabled && entries.length === 0)}
            style={{
              padding: '12px 24px',
              backgroundColor: (!enabled && entries.length === 0) 
                ? theme.colors.border 
                : (enabled ? theme.colors.error : theme.colors.success),
              color: (!enabled && entries.length === 0) ? theme.colors.textMuted : '#ffffff',
              border: 'none',
              borderRadius: theme.radius.md,
              fontSize: '0.9375rem',
              fontWeight: 600,
              cursor: (toggling || loading || (!enabled && entries.length === 0)) ? 'not-allowed' : 'pointer',
              opacity: toggling || loading ? 0.7 : 1,
              minWidth: '140px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            {toggling ? (
              <>
                <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
                {enabled ? 'Disabling...' : 'Enabling...'}
              </>
            ) : enabled ? (
              'Disable Restrictions'
            ) : (
              'Enable Restrictions'
            )}
          </button>
        </div>

        {!enabled && entries.length === 0 && (
          <div
            style={{
              marginTop: '16px',
              padding: '12px 16px',
              backgroundColor: '#fef3c7',
              borderRadius: theme.radius.sm,
              borderLeft: `4px solid #f59e0b`,
            }}
          >
            <p style={{ margin: 0, fontSize: '0.8125rem', color: '#92400e' }}>
              <strong>Step 1:</strong> Add at least one IP address to the allowlist below before you can enable restrictions.
            </p>
          </div>
        )}
        {!enabled && entries.length > 0 && (
          <div
            style={{
              marginTop: '16px',
              padding: '12px 16px',
              backgroundColor: theme.colors.successBg,
              borderRadius: theme.radius.sm,
              borderLeft: `4px solid ${theme.colors.success}`,
            }}
          >
            <p style={{ margin: 0, fontSize: '0.8125rem', color: theme.colors.success }}>
              <strong>Ready:</strong> You have {entries.length} IP address{entries.length > 1 ? 'es' : ''} in your allowlist. Click Enable Restrictions to activate.
            </p>
          </div>
        )}
        {enabled && (
          <div
            style={{
              marginTop: '16px',
              padding: '12px 16px',
              backgroundColor: '#fef3c7',
              borderRadius: theme.radius.sm,
              borderLeft: `4px solid #f59e0b`,
            }}
          >
            <p style={{ margin: 0, fontSize: '0.8125rem', color: '#92400e' }}>
              <strong>Warning:</strong> Removing all IPs or disabling restrictions will allow anyone to access your workspace. You can always manage this from my.dashboardfox.app if locked out.
            </p>
          </div>
        )}
      </div>

      {/* Current IP Info */}
      {yourIp && (
        <div
          style={{
            backgroundColor: theme.colors.bg,
            borderRadius: theme.radius.lg,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: `1px solid ${theme.colors.border}`,
            borderLeft: `4px solid ${theme.colors.primary}`,
            padding: '16px 24px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Monitor size={20} strokeWidth={1.5} color={theme.colors.primary} />
            <div>
              <span style={{ fontSize: '0.8125rem', color: theme.colors.textMuted }}>Your current IP address:</span>
              <span
                style={{
                  marginLeft: '8px',
                  fontFamily: theme.fontMono,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: theme.colors.text,
                }}
              >
                {yourIp}
              </span>
            </div>
          </div>
          {!isCurrentIpInList && (
            <button
              onClick={handleAddCurrentIp}
              disabled={adding}
              style={{
                padding: '6px 12px',
                backgroundColor: theme.colors.primaryLight,
                color: theme.colors.primary,
                border: 'none',
                borderRadius: theme.radius.sm,
                fontSize: '0.8125rem',
                fontWeight: 500,
                cursor: adding ? 'not-allowed' : 'pointer',
              }}
            >
              {adding ? 'Adding...' : 'Add to allowlist'}
            </button>
          )}
          {isCurrentIpInList && (
            <span
              style={{
                padding: '4px 10px',
                backgroundColor: theme.colors.successBg,
                color: theme.colors.success,
                borderRadius: theme.radius.sm,
                fontSize: '0.75rem',
                fontWeight: 600,
              }}
            >
              ✓ In allowlist
            </span>
          )}
        </div>
      )}

      {/* Add IP Card */}
      <div
        style={{
          backgroundColor: theme.colors.bg,
          borderRadius: theme.radius.lg,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: `1px solid ${theme.colors.border}`,
          padding: '24px',
          marginBottom: '24px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '20px',
            paddingBottom: '16px',
            borderBottom: `1px solid ${theme.colors.border}`,
          }}
        >
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: theme.radius.md,
              backgroundColor: theme.colors.primaryLight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Plus size={18} strokeWidth={2} color={theme.colors.primary} />
          </div>
          <span style={{ fontSize: '0.9375rem', fontWeight: 600, color: theme.colors.text }}>Add IP Address</span>
        </div>

        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '0.6875rem',
                fontWeight: 600,
                color: theme.colors.textMuted,
                marginBottom: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              IP Address or CIDR
            </label>
            <input
              type="text"
              placeholder="192.168.1.1 or 10.0.0.0/24"
              value={newIp}
              onChange={(e) => setNewIp(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddEntry()}
              style={{
                width: '100%',
                padding: '10px 12px',
                backgroundColor: theme.colors.bg,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: theme.radius.sm,
                fontSize: '0.875rem',
                color: theme.colors.text,
                fontFamily: theme.fontMono,
                fontWeight: 500,
                outline: 'none',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = theme.colors.primary;
                e.target.style.boxShadow = `0 0 0 3px ${theme.colors.primaryLight}`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = theme.colors.border;
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
          <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '0.6875rem',
                fontWeight: 600,
                color: theme.colors.textMuted,
                marginBottom: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              Label (optional)
            </label>
            <input
              type="text"
              placeholder="e.g., Office, Home, VPN"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddEntry()}
              style={{
                width: '100%',
                padding: '10px 12px',
                backgroundColor: theme.colors.bg,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: theme.radius.sm,
                fontSize: '0.875rem',
                color: theme.colors.text,
                outline: 'none',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = theme.colors.primary;
                e.target.style.boxShadow = `0 0 0 3px ${theme.colors.primaryLight}`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = theme.colors.border;
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
          <button
            onClick={handleAddEntry}
            disabled={adding || !newIp.trim()}
            onMouseEnter={() => setAddBtnHover(true)}
            onMouseLeave={() => setAddBtnHover(false)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '10px 24px',
              minWidth: '120px',
              backgroundColor:
                adding || !newIp.trim()
                  ? theme.colors.border
                  : addBtnHover
                    ? theme.colors.primaryHover
                    : theme.colors.primary,
              color: adding || !newIp.trim() ? theme.colors.textMuted : '#ffffff',
              border: 'none',
              borderRadius: theme.radius.md,
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: adding || !newIp.trim() ? 'not-allowed' : 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            {adding ? (
              <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <Plus size={16} strokeWidth={2} />
            )}
            {adding ? 'Adding...' : 'Add'}
          </button>
        </div>
      </div>

      {/* Info Card */}
      <div
        style={{
          backgroundColor: theme.colors.bg,
          borderRadius: theme.radius.lg,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: `1px solid ${theme.colors.border}`,
          borderLeft: `4px solid ${theme.colors.primary}`,
          padding: '24px',
          marginBottom: '24px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
          <Info size={18} strokeWidth={1.5} color={theme.colors.primary} />
          <span style={{ fontSize: '0.9375rem', fontWeight: 600, color: theme.colors.text }}>
            How IP Restrictions Work
          </span>
        </div>
        <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.875rem', color: theme.colors.textMuted, lineHeight: 1.8 }}>
          <li>When enabled, only IP addresses in the allowlist can access your workspace</li>
          <li>Supports both single IPs (192.168.1.1) and CIDR ranges (10.0.0.0/24)</li>
          <li>Changes take effect within 1-2 minutes</li>
          <li>You can always manage restrictions from my.dashboardfox.app even if locked out</li>
        </ul>
      </div>

      {/* IP List */}
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '96px 0', gap: '12px' }}>
          <Loader2 size={32} strokeWidth={2} color={theme.colors.primary} style={{ animation: 'spin 1s linear infinite' }} />
          <span style={{ color: theme.colors.textTertiary, fontSize: '0.875rem' }}>Loading...</span>
        </div>
      ) : entries.length === 0 ? (
        <div
          style={{
            backgroundColor: theme.colors.bg,
            borderRadius: theme.radius.lg,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: `1px solid ${theme.colors.border}`,
            padding: '64px',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              width: '64px',
              height: '64px',
              borderRadius: theme.radius.lg,
              backgroundColor: theme.colors.primaryLight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}
          >
            <Shield size={28} strokeWidth={1.5} color={theme.colors.primary} />
          </div>
          <h3 style={{ margin: '0 0 8px', fontSize: '1.0625rem', color: theme.colors.text, fontWeight: 600 }}>
            No IP addresses in allowlist
          </h3>
          <p style={{ margin: 0, fontSize: '0.875rem', color: theme.colors.textTertiary }}>
            Add IP addresses above to restrict access to your workspace
          </p>
        </div>
      ) : (
        <div
          style={{
            backgroundColor: theme.colors.bg,
            borderRadius: theme.radius.lg,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: `1px solid ${theme.colors.border}`,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: '16px 20px',
              borderBottom: `1px solid ${theme.colors.border}`,
              backgroundColor: theme.colors.bgAlt,
            }}
          >
            <h3 style={{ margin: 0, fontSize: '0.9375rem', fontWeight: 600, color: theme.colors.text }}>
              Allowed IP Addresses ({entries.length})
            </h3>
          </div>
          {entries.map((entry, index) => (
            <div
              key={entry.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '16px 20px',
                gap: '16px',
                backgroundColor: hoveredRow === entry.id ? theme.colors.bgAlt : theme.colors.bg,
                borderBottom: index < entries.length - 1 ? `1px solid ${theme.colors.border}` : 'none',
                transition: 'background-color 0.1s ease',
              }}
              onMouseEnter={() => setHoveredRow(entry.id)}
              onMouseLeave={() => setHoveredRow(null)}
            >
              {/* IP Address */}
              <div style={{ flex: '0 0 200px' }}>
                <code
                  style={{
                    fontFamily: theme.fontMono,
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: theme.colors.text,
                  }}
                >
                  {entry.ip_cidr}
                </code>
                {entry.ip_cidr === yourIp && (
                  <span
                    style={{
                      marginLeft: '8px',
                      padding: '2px 6px',
                      backgroundColor: theme.colors.primaryLight,
                      color: theme.colors.primary,
                      borderRadius: '4px',
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                    }}
                  >
                    YOU
                  </span>
                )}
              </div>

              {/* Label */}
              <div style={{ flex: 1 }}>
                {editingId === entry.id ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="text"
                      value={editLabel}
                      onChange={(e) => setEditLabel(e.target.value)}
                      placeholder="Add a label..."
                      autoFocus
                      style={{
                        flex: 1,
                        padding: '6px 10px',
                        backgroundColor: theme.colors.bg,
                        border: `1px solid ${theme.colors.primary}`,
                        borderRadius: theme.radius.sm,
                        fontSize: '0.875rem',
                        color: theme.colors.text,
                        outline: 'none',
                        boxShadow: `0 0 0 3px ${theme.colors.primaryLight}`,
                      }}
                      onKeyPress={(e) => e.key === 'Enter' && handleSaveEdit(entry.id)}
                      onKeyDown={(e) => e.key === 'Escape' && cancelEdit()}
                    />
                    <button
                      onClick={() => handleSaveEdit(entry.id)}
                      disabled={saving}
                      style={{
                        padding: '6px',
                        backgroundColor: theme.colors.success,
                        color: '#fff',
                        border: 'none',
                        borderRadius: theme.radius.sm,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {saving ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={14} />}
                    </button>
                    <button
                      onClick={cancelEdit}
                      disabled={saving}
                      style={{
                        padding: '6px',
                        backgroundColor: theme.colors.bgAlt,
                        color: theme.colors.textMuted,
                        border: `1px solid ${theme.colors.border}`,
                        borderRadius: theme.radius.sm,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <span style={{ fontSize: '0.875rem', color: entry.label ? theme.colors.text : theme.colors.textTertiary }}>
                    {entry.label || '—'}
                  </span>
                )}
              </div>

              {/* Actions */}
              {editingId !== entry.id && (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => startEdit(entry)}
                    title="Edit label"
                    onMouseEnter={() => setHoveredButton(`edit-${entry.id}`)}
                    onMouseLeave={() => setHoveredButton(null)}
                    style={{
                      padding: '8px',
                      backgroundColor: hoveredButton === `edit-${entry.id}` ? theme.colors.primaryLight : theme.colors.bgAlt,
                      color: hoveredButton === `edit-${entry.id}` ? theme.colors.primary : theme.colors.textTertiary,
                      border: 'none',
                      borderRadius: theme.radius.sm,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Edit2 size={14} strokeWidth={1.5} />
                  </button>
                  <button
                    onClick={() => openDeleteModal(entry)}
                    title="Remove"
                    onMouseEnter={() => setHoveredButton(`delete-${entry.id}`)}
                    onMouseLeave={() => setHoveredButton(null)}
                    style={{
                      padding: '8px',
                      backgroundColor: hoveredButton === `delete-${entry.id}` ? theme.colors.errorBg : theme.colors.bgAlt,
                      color: hoveredButton === `delete-${entry.id}` ? theme.colors.error : theme.colors.textTertiary,
                      border: 'none',
                      borderRadius: theme.radius.sm,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Trash2 size={14} strokeWidth={1.5} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.open && deleteModal.entry && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeDeleteModal();
          }}
        >
          <div
            style={{
              backgroundColor: theme.colors.bg,
              borderRadius: theme.radius.lg,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              border: `1px solid ${theme.colors.border}`,
              maxWidth: '400px',
              width: '90%',
              overflow: 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: theme.radius.md,
                    backgroundColor: theme.colors.errorBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AlertTriangle size={20} strokeWidth={1.5} color={theme.colors.error} />
                </div>
                <div>
                  <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, color: theme.colors.text }}>
                    Remove IP Address
                  </h2>
                </div>
              </div>

              <p style={{ fontSize: '0.875rem', color: theme.colors.textMuted, marginBottom: '16px' }}>
                Are you sure you want to remove{' '}
                <code style={{ fontFamily: theme.fontMono, fontWeight: 600, color: theme.colors.text }}>
                  {deleteModal.entry.ip_cidr}
                </code>
                {deleteModal.entry.label && ` (${deleteModal.entry.label})`} from the allowlist?
              </p>

              {deleteModal.entry.ip_cidr === yourIp && (
                <div
                  style={{
                    padding: '12px',
                    backgroundColor: '#fef3c7',
                    borderRadius: theme.radius.sm,
                    marginBottom: '16px',
                  }}
                >
                  <p style={{ margin: 0, fontSize: '0.8125rem', color: '#92400e' }}>
                    <strong>Warning:</strong> This is your current IP address. Removing it while restrictions are enabled may lock you out.
                  </p>
                </div>
              )}
            </div>

            <div
              style={{
                padding: '16px 24px',
                borderTop: `1px solid ${theme.colors.border}`,
                backgroundColor: theme.colors.bgAlt,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
              }}
            >
              <button
                onClick={closeDeleteModal}
                disabled={deleting}
                style={{
                  padding: '10px 20px',
                  backgroundColor: theme.colors.bg,
                  color: theme.colors.textMuted,
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: theme.radius.md,
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: deleting ? 'not-allowed' : 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteEntry}
                disabled={deleting}
                style={{
                  padding: '10px 20px',
                  backgroundColor: theme.colors.error,
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: theme.radius.md,
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {deleting ? (
                  <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <Trash2 size={16} strokeWidth={1.5} />
                )}
                {deleting ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default IPRestrictionsCore;
